import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { filter, map, tap } from "rxjs/operators";
import { EnumStringConverter } from "src/components/shared-components/enum/enum-string-converter";
import { Environment } from "src/components/shared-components/class/general-classes/environment";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import {
  EnvironmentCategory,
  EnvironmentType,
} from "src/components/shared-components/enum/enumEnvironment";
import { ManufacturerSystem } from "src/components/shared-components/enum/enumMisc";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { EnvironmentService } from "src/service/environment.service";
import { NetworkService } from "src/service/network.service";
import { listEnvironment } from "src/state/selectors/environment.selectors";

@Component({
    selector: "app-list-environments",
    templateUrl: "./list-environments.component.html",
    styleUrls: ["./list-environments.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ListEnvironmentsComponent implements OnInit, iTranslation {
  private _environmentsStoreSubscription: Subscription = null;
  private _networkServiceSubscription: Subscription = null;
  private _translateSubscription: Subscription = null;

  public listEnvironment: Array<Environment> = new Array<Environment>();

  public isEnvironmentLoaded: boolean = false;
  public showErrorContent: boolean = false;

  public searchTextRoom: string = "";
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public toolBarColor: string = "primary";

  public tabEnvironmentSelected: string = "";
  public listEnvironmentCategoryTab: Array<any> = new Array<any>();
  public listFilteredEnvironment: Array<Environment> = new Array<Environment>();
  public listSelectedEnvironment: Array<Environment> = new Array<Environment>();
  public groupedEnvironment: object = null;
  public lblSearchEnvironments: string = "";

  @Input() showCommonAreaEnvironment: boolean;
  @Input() showCustomEnvironment: boolean;
  @Input() showEnvironmentForAccessControl: boolean;
  @Input() showRoomEnvironment: boolean;
  @Input() showSuite: boolean;
  @Input() allowMultipleSelection: boolean;

  @Output() onListEnvironmentSelectedUpdate: EventEmitter<Array<Environment>> =
    new EventEmitter<Array<Environment>>();

  constructor(
    private _store: Store,
    private _networkService: NetworkService,
    private _environmentService: EnvironmentService,
    private _translateService: TranslateService
  ) { }

  /**
   * ngOnInit: subscribe environmentList
   */
  ngOnInit() {
    this.handleFallbackInputParameters();
    this.loadTranslation();
    this.subscribeTranslationEvent();

    this.loadListEnvironment();
    this.subscribeNetworkServiceData();
  }

  ngOnDestroy() {
    this.unsubscribeStoreSelect();

    this._networkServiceSubscription?.unsubscribe();
    this._translateSubscription?.unsubscribe();
  }

  /**
   * Handle click environment selection/unselection.
   *
   * @param environment Environment to check
   */
  public onClickSelectUnselectEnvironment(environment: Environment): void {
    const indexEnvironment: number = this.listSelectedEnvironment.findIndex(
      (env: Environment) => env.id === environment.id
    );

    if (indexEnvironment >= 0) {
      this.listSelectedEnvironment.splice(indexEnvironment, 1);

      this.onListEnvironmentSelectedUpdate.emit(this.listSelectedEnvironment);
    } else {
      if (!this.allowMultipleSelection) {
        this.listSelectedEnvironment = [environment];

        this.onListEnvironmentSelectedUpdate.emit(this.listSelectedEnvironment);
      } else {
        this.listSelectedEnvironment = [
          ...this.listSelectedEnvironment,
          environment,
        ];

        this.onListEnvironmentSelectedUpdate.emit(this.listSelectedEnvironment);
      }
    }
  }

  /**
   * This method reset the list containing the currently selected environments.
   */
  public resetEnvironmentSelection(): void {
    this.listSelectedEnvironment = new Array<Environment>();
  }

  /**
   * handle click environment category tab
   *
   * @param clickedEnvironmentCategory Cliecked category
   */
  public onClickTabEnvironmentCategory(
    clickedEnvironmentCategory: string
  ): void {
    this.openTabBySelectedCategory(clickedEnvironmentCategory);
  }

  /**
   * Perform to open single tab by selected category.
   * It set also environmnet tab selected to track selected etab
   *
   * @param selectedCategory
   */
  private openTabBySelectedCategory(selectedCategory: string) {
    this.tabEnvironmentSelected = selectedCategory;

    this.loadFilteredEnvironment(this.tabEnvironmentSelected);
  }

  /**
   * Get color style selectio if current environment is in listSelectedEnvironment list
   *
   * @param enviromnet Environment to check
   * @returns
   */
  public getColorByEnvironmentSelection(environment: Environment): string {
    const foundedEnvironment: Environment = this.listSelectedEnvironment.find(
      (env: Environment) => env.id === environment.id
    );

    return foundedEnvironment ? this.toolBarColor : "";
  }

  //#region Translation
  /**
   * Load translation from translation service
   */
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.searchEnvironment"])
      .subscribe((translation) => {
        this.lblSearchEnvironments =
          translation["dictionary.searchEnvironment"];
      });
  }

  /**
   * Subscribe translation to translation event
   */
  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Load list environment by specific key.
   * If filtered groupedEnvironment object by specific key (es. 2|0) contained in keyToLoad parameter
   *
   * @param keyToLoad Parameter to load
   */
  private loadFilteredEnvironment(keyToLoad: string): void {
    this.listFilteredEnvironment = this.groupedEnvironment[keyToLoad];
  }

  /**
   *  Loa full list of environments base  on filter parameter
   */
  private loadListEnvironment(): void {
    this._environmentsStoreSubscription = this._store
      .select(listEnvironment)
      .pipe(
        filter(
          (listEnvironmentObject: GenericStoreApiData) =>
            listEnvironmentObject !== null
        ),
        tap(
          (listEnvironmentObject: GenericStoreApiData) =>
            (this.isEnvironmentLoaded = listEnvironmentObject.loaded)
        ),
        filter(
          (listEnvironmentObject: GenericStoreApiData) =>
            listEnvironmentObject.loaded
        ),
        filter(
          (listEnvironmentObject: GenericStoreApiData) =>
            !listEnvironmentObject.errorGeneric
        ),
        map(
          (listEnvironmentObject: GenericStoreApiData) =>
            listEnvironmentObject.data?.data || []
        ),
        map((listEnvironment: Array<Environment>) => {
          return listEnvironment.filter(
            (environment) =>
              environment.environmentType == EnvironmentType.ROOM ||
              environment.environmentType == EnvironmentType.SUITE ||
              environment.environmentType == EnvironmentType.CUSTOMIZED
          );
        }),
        map((listEnvironment: Array<Environment>) =>
          listEnvironment.filter((environment: Environment) =>
            this.filterEnvironmentByInputParameter(environment)
          )
        ),
        map((listEnvironment: Array<Environment>) => {
          if (this.showEnvironmentForAccessControl) {
            return listEnvironment.filter(
              (environment) =>
                environment.accessControlManufacturerSystem !==
                ManufacturerSystem.NONE &&
                environment.accessControlManufacturerSystem !==
                ManufacturerSystem.UNKNOWN
            );
          } else {
            return listEnvironment;
          }
        }),
        tap(
          (listEnvironment: Array<Environment>) =>
            (this.listEnvironment = listEnvironment)
        ),
        map((listEnvironment: Array<Environment>) => {
          return this._environmentService.groupEnvironmentByTypeAndCategory(
            listEnvironment
          );
        })
      )
      .subscribe((groupedEnvironemnt: object) => {
        this.groupedEnvironment = groupedEnvironemnt || null;

        if (groupedEnvironemnt) {
          this.buildTabsByEnvironmentCategory(groupedEnvironemnt);
        }
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable

  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._environmentsStoreSubscription?.unsubscribe();
  }

  //#endregion

  /**
   * Build segment array to render ion-segment in ui based on grouped environment
   *
   * @param groupedEnvironemnt Object for group environment
   */
  private buildTabsByEnvironmentCategory(groupedEnvironemnt: object): void {
    let avaiableTabIdenfier: Array<string> = Object.keys(groupedEnvironemnt);
    this.listEnvironmentCategoryTab = new Array<any>();

    for (const key of avaiableTabIdenfier) {
      this.listEnvironmentCategoryTab.push(
        this.buildSingleTabObject(Number(key))
      );
    }

    this.openTabBySelectedCategory(avaiableTabIdenfier[0]);
  }

  /**
   * Build single tab object by environment category
   *
   * @param environmentCategory Environment category object
   * @returns
   */
  private buildSingleTabObject(
    environmentCategory: EnvironmentCategory
  ): object {
    return {
      label:
        EnumStringConverter.environmentCategoryToStringEnum(
          environmentCategory,
          this._translateService,
          true
        ) +
        "(" +
        this.getTabListCount(environmentCategory.toLocaleString()) +
        ")",
      icon: this.getTabIconClassByCategory(environmentCategory.toString()),
      category: environmentCategory.toString(),
    };
  }

  /**
   * Generate single tab icon by key segment value
   *
   * @param keySegment Key segment to get css icon class
   * @returns Css icon class
   */
  public getTabIconClassByCategory(keySegment: string): string {
    let cssSegmentIconClass: string = "";

    switch (Number(keySegment)) {
      case EnvironmentCategory.ROOM:
      case EnvironmentCategory.SUITE:
      case EnvironmentCategory.JUNIOR_SUITE:
      case EnvironmentCategory.SINGLE_ROOM:
      case EnvironmentCategory.DOUBLE_ROOM:
      case EnvironmentCategory.TRIPLE_ROOM:
      case EnvironmentCategory.FAMILY_ROOM:
      case EnvironmentCategory.DORMITORY:
        cssSegmentIconClass = "bed";
        break;
      case EnvironmentCategory.COMMON_AREA:
      case EnvironmentCategory.EXTERNAL_ACCESS:
        cssSegmentIconClass = "cube";
        break;
      case EnvironmentCategory.CABINETS:
      case EnvironmentCategory.ELAVATOR_FLOOR:
      case EnvironmentCategory.SKI_DEPOSITS:
        cssSegmentIconClass = "grid";
        break;
    }

    return cssSegmentIconClass;
  }

  /**
   * Return segment list count by environment list
   *
   * @param keySegment key segment to get list environment count
   * @returns List count as string
   */
  public getTabListCount(keySegment: string): string {
    const listEnvironment: Array<Environment> =
      this.groupedEnvironment[keySegment];

    return listEnvironment.length > 99
      ? "+99"
      : listEnvironment.length.toString();
  }

  /**
   * Handle fallback input parameter
   */
  private handleFallbackInputParameters(): void {
    this.showCommonAreaEnvironment = this.showCommonAreaEnvironment ??= true;
    this.showRoomEnvironment = this.showRoomEnvironment ??= true;
    this.showCustomEnvironment = this.showCustomEnvironment ??= true;
    this.allowMultipleSelection = this.allowMultipleSelection ??= false;
    this.showSuite = this.showSuite ??= true;
  }

  /**
   * Filter environment by input parameter
   *
   * @param environment Environment to filter
   * @returns
   */
  private filterEnvironmentByInputParameter(environment: Environment): boolean {
    switch (environment.environmentType) {
      case EnvironmentType.COMMON_AREA:
        return this.showCommonAreaEnvironment;
        break;
      case EnvironmentType.ROOM:
        return this.showRoomEnvironment;
        break;
      case EnvironmentType.CUSTOMIZED:
        return this.showCustomEnvironment;
        break;
      case EnvironmentType.SUITE:
        return this.showSuite;
        break;
    }
  }

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }
}
